import { useEffect, useRef, useState } from "react";
import { useToast } from "../../hooks/toast";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import {
  AutoFaucetStatusEvent,
  AutoFaucetTransaction,
  subscribeAutomaticFaucet,
} from "../../hooks/api/faucet/useAutomaticFaucet";
import { ChainIdEnum } from "../../enums/chain";
import FaucetOnboardingDialog from "../FaucetOnboardingDialog";

const FUNDING_TOAST_MS = 12000;
const FUNDED_TOAST_MS = 8000;

interface DialogState {
  transactions?: AutoFaucetTransaction[];
  chainId?: ChainIdEnum;
}

const summarizeLegs = (transactions?: AutoFaucetTransaction[]): string => {
  const legs = (transactions ?? []).filter((tx) => tx && tx.token && tx.amount);
  // The faucet pays the wallet, not the trading account: say what to do next.
  if (legs.length === 0)
    return "Testnet funds are in your wallet. Deposit them to start trading.";
  return `${legs.map((tx) => `${tx.amount} ${tx.token}`).join(" and ")} sent to your wallet. Deposit to start trading.`;
};

/**
 * Bridges automatic-faucet lifecycle events to onboarding UI: a non-blocking
 * "funds are on the way" alert while the transfer confirms, then a success alert
 * plus a dialog once the funds land. Rendered once, inside the toast + wallet
 * providers. Funding itself is driven by useAutomaticFaucet() and stays
 * best-effort — this component only reflects it and never blocks login.
 */
const FaucetOnboardingNotifier = () => {
  const { addToast } = useToast();
  const [dialog, setDialog] = useState<DialogState | null>(null);

  // The subscription is registered once; keep the latest addToast in a ref so it
  // never captures a stale toast queue.
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  useEffect(() => {
    // Track which addresses already showed a hint/success so a reconnect or an
    // in-progress poll doesn't spam duplicate toasts.
    const funded = new Set<string>();

    const handle = (event: AutoFaucetStatusEvent) => {
      const key = `${event.chainId}:${event.address.toLowerCase()}`;

      if (event.phase === "funding") {
        addToastRef.current(
          {
            type: ToastEnum.SIMPLE,
            status: ToastStatusEnum.SUCCESS,
            header: "Setting up your testnet balance",
            subheader:
              "We noticed your wallet is low on testnet funds, so we're sending some over. This takes a few seconds — no action needed.",
          },
          FUNDING_TOAST_MS
        );
        return;
      }

      if (event.phase === "funded") {
        if (funded.has(key)) return;
        funded.add(key);
        addToastRef.current(
          {
            type: ToastEnum.SIMPLE,
            status: ToastStatusEnum.SUCCESS,
            header: "Testnet funds sent",
            subheader: summarizeLegs(event.transactions),
          },
          FUNDED_TOAST_MS
        );
        setDialog({
          transactions: event.transactions,
          chainId: event.chainId as ChainIdEnum,
        });
      }
      // `sufficient` and `error` are intentionally silent: the wallet was already
      // funded, or funding failed and must not surface a blocking error here.
    };

    const unsubscribe = subscribeAutomaticFaucet(handle);
    return unsubscribe;
  }, []);

  return (
    <FaucetOnboardingDialog
      open={dialog !== null}
      transactions={dialog?.transactions}
      chainId={dialog?.chainId}
      onClose={() => setDialog(null)}
    />
  );
};

export default FaucetOnboardingNotifier;
